import { useState, useCallback, useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { debounce, orderBy } from 'lodash';
import parsePhoneNumber from 'libphonenumber-js';
import { PERMISSIONS } from '@repo/shared/domain';
import { Role } from '@repo/shared/domain';
import { Task } from '@repo/shared/domain';
import { User, InviteData, ImportData } from '@repo/shared/domain';
import { Document } from '@repo/shared/domain';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import {
	getPartnerCompany,
	getPartnerCompanyUsers,
	getPartnerCompanyRoles,
	getPartnerCompanyUserTasks,
	getPartnerCompanyUserApprovals,
	getPartnerCompanyUserDocuments,
	getPartnerCompanyUserProfile,
	linkUserToPartnerCompany,
	importUsersToPartnerCompany,
	archivePartnerCompanyUser,
	updatePartnerCompanyUser,
	addRoleToPartnerCompanyUser,
	removeRoleFromPartnerCompanyUser,
	resendInviteToPartnerCompanyUser,
	getPartnerCompanyUserRolesWithTasks,
} from 'src/api/partner';
import { getVerifyPermission, importUserFailureFromCaughtError } from '@repo/shared/helpers';
import { EventEmitter } from '@repo/shared/helpers';
import { constructQuery } from '@repo/shared/helpers';
import { sortToString, stringToSort } from '@repo/shared/helpers';
import { Button } from '@repo/components';
import { Badge } from '@repo/components';
import { Spinner } from '@repo/components';
import { Alert } from '@repo/components';
import { FilterAutoCompleteById } from '@repo/components';
import { FilterSearch } from '@repo/components';
import { FilterSelect } from '@repo/components';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@repo/components';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/components';
import { GeneralTooltip as Tooltip } from '@repo/components';
import { DataGrid, DataGridColumn } from '@repo/components';
import { ConfirmDialog } from '@repo/components';
import { RoleChip } from '@repo/components';
import { ConditionalWrapper } from '@repo/components';
import { TaskDialog } from '@repo/components';
import { DocumentViewer } from '@repo/components';
import { InviteForm } from '@repo/forms';
import { UserForm } from '@repo/forms';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { AddToRoleDialog } from '@repo/components';
import UserRolesWithTasksAccordion from 'src/parts/users/UserRolesWithTasksAccordion';
import { useConfig } from 'src/hooks/useConfig';

import { Plus, Edit, Trash2, AlertTriangle, XCircle, Clock, CheckCircle, X, User as UserIcon } from 'lucide-react';

const { COUNTRY, NODE_ENV } = process.env;

type UserSearchSortOptions = 'firstName' | 'updatedAt' | 'lastName';
type SortDirection = 'asc' | 'desc';
type UserSearchSort = `${UserSearchSortOptions}:${SortDirection}`;

type UserSearch = {
	search: string;
	roleIds: string | undefined;
	page: number;
	sort: UserSearchSort;
	roleStatus: string;
	activeUserId: string;
};

export const Route = createFileRoute('/partner_/$partnerId/users/')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>): UserSearch => ({
		search: (search.search as string) || '',
		roleIds: (search.roleIds as string) || undefined,
		page: Number(search.page) || 0,
		sort: (typeof search.sort === 'string' ? (search.sort as UserSearchSort) : undefined) || 'updatedAt:desc',
		roleStatus: (search.roleStatus as string) || 'all',
		activeUserId: (search.activeUserId as string) || '',
	}),
});

const variants = {
	done: 'success',
	accepted: 'success',
	pending: 'default',
	todo: 'default',
	expiryWarning: 'warning',
	expired: 'destructive',
	failed: 'destructive',
	inReview: 'destructive',
	declined: 'destructive',
} as const;

const roleStatusTypes = [
	{ value: 'all', label: 'All' },
	{ value: 'pending', label: 'Pending' },
	{ value: 'accepted', label: 'Accepted' },
	{ value: 'expired', label: 'Expired' },
	{ value: 'expiryWarning', label: 'Expiry Warning' },
	{ value: 'archived', label: 'Archived' },
];

export const FileState = ({ status = 'accepted', archived = false }) => {
	if (archived) {
		return <XCircle className="h-4 w-4 text-gray-500" />;
	}

	switch (status) {
		case 'pending':
			return <Clock className="h-4 w-4 text-blue-500" />;
		case 'accepted':
			return <CheckCircle className="h-4 w-4 text-green-500" />;
		case 'inReview':
			return <AlertTriangle className="h-4 w-4 text-red-500" />;
		case 'declined':
			return <XCircle className="h-4 w-4 text-red-500" />;
		case 'expiryWarning':
			return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
		case 'expired':
			return <Clock className="h-4 w-4 text-red-500" />;
		case 'superseded':
			return <Clock className="h-4 w-4 text-gray-500" />;
		default:
			return <></>;
	}
};

function RouteComponent() {
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const { config } = useConfig();
	const { partnerId } = Route.useParams();
	useBreadcrumbLevel(1, 'Users');
	const { search, roleIds, page, sort, roleStatus, activeUserId } = Route.useSearch();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const defaultCountry = config?.defaultCountry || COUNTRY || 'BE';

	// State management
	const [activeUser, setActiveUser] = useState<User | undefined>();
	const [activeUserTasks, setActiveUserTasks] = useState<Task[]>([]);
	const [activeUserTodoTasks, setActiveUserTodoTasks] = useState<Task[]>([]);
	const [activeUserApprovals, setActiveUserApprovals] = useState<Task[]>([]);
	const [activeUserTodoApprovals, setActiveUserTodoApprovals] = useState<Task[]>([]);
	const [activeUserDocuments, setActiveUserDocuments] = useState<Document[]>([]);
	const [activeUserProfile, setActiveUserProfile] = useState<any>();
	const [selectedFile, setSelectedFile] = useState<Document | null>(null);
	const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
	const [activeTask, setActiveTask] = useState<Task | undefined>();
	const [isEditingRoles, setIsEditingRoles] = useState(false);
	const [isEditingUser, setIsEditingUser] = useState(false);
	const [isDeleteUserDialogOpen, setIsDeleteUserDialogOpen] = useState(false);
	const [roleToRevokeId, setRoleToRevokeId] = useState<string | undefined>();
	const [isRoleDialogOpen, setRoleDialogOpen] = useState(false);
	const [isInviteSent, setIsInviteSent] = useState(false);
	const [isSendingInvite, setIsSendingInvite] = useState(false);
	const [rolesWithTasks, setRolesWithTasks] = useState<any[]>([]);

	const isSheetOpen = !!activeUserId;

	// Queries
	const { data: partnerCompany, isLoading: companyLoading } = useQuery({
		queryKey: ['partner-company', partnerId],
		queryFn: () => getPartnerCompany(partnerId),
		enabled: !userLoading && !!authenticatedUser,
	});

	const { data: usersResponse, isLoading: usersLoading } = useQuery({
		queryKey: ['partner-company-users', partnerId, { search, roleIds, page, sort, roleStatus }],
		queryFn: () =>
			getPartnerCompanyUsers(partnerId, constructQuery({ search, roleIds, page: page.toString(), sort, roleStatus })),
		enabled: !userLoading && !!authenticatedUser,
	});

	const { data: roles = [], isLoading: rolesLoading } = useQuery({
		queryKey: ['partner-company-roles', partnerId],
		queryFn: () => getPartnerCompanyRoles(partnerId),
		enabled: !userLoading && !!authenticatedUser,
	});
	const orderedRoles = orderBy(roles, ['usesManager', 'name']).map((r) => ({
		...r,
		group: r.usesManager ? 'System Role' : 'User Role',
	}));

	const updateUserDocuments = useCallback(
		async (userId: string) => {
			try {
				const userDocs = await getPartnerCompanyUserDocuments(partnerId, userId, 'includeLogs=true');
				setActiveUserDocuments(userDocs.data || []);
			} catch (error) {
				console.error('Error loading user documents:', error);
			}
		},
		[partnerId],
	);

	const handleRowClick = useCallback(
		async ({ id }: { id: string }) => {
			navigate({
				search: (prev) => ({ ...prev, activeUserId: id }),
			});

			setIsInviteSent(false);
			setIsSendingInvite(false);
			const user = usersResponse?.data.find((u) => u.id === id);
			setActiveUser(user);

			if (user) {
				const [tasksResult, approvalsResult, profileResult, rolesWithTasksResult] = await Promise.allSettled([
					getPartnerCompanyUserTasks(partnerId, id),
					getPartnerCompanyUserApprovals(partnerId, id),
					getPartnerCompanyUserProfile(partnerId, id),
					getPartnerCompanyUserRolesWithTasks(partnerId, id),
				]);

				if (tasksResult.status === 'fulfilled') {
					const userTasks = tasksResult.value.data || [];
					setActiveUserTodoTasks(userTasks.filter((task) => task.status === 'todo'));
					setActiveUserTasks(userTasks);
				} else {
					console.error('Error loading user tasks:', tasksResult.reason);
					setActiveUserTasks([]);
					setActiveUserTodoTasks([]);
				}

				if (approvalsResult.status === 'fulfilled') {
					const userApprovals = approvalsResult.value.data || [];
					setActiveUserTodoApprovals(userApprovals.filter((approval) => approval.status === 'todo'));
					setActiveUserApprovals(userApprovals);
				} else {
					console.error('Error loading user approvals:', approvalsResult.reason);
					setActiveUserApprovals([]);
					setActiveUserTodoApprovals([]);
				}

				if (profileResult.status === 'fulfilled') {
					setActiveUserProfile(profileResult.value);
				} else {
					console.error('Error loading user profile:', profileResult.reason);
					setActiveUserProfile(undefined);
				}

				if (rolesWithTasksResult.status === 'fulfilled') {
					setRolesWithTasks(rolesWithTasksResult.value.data);
				} else {
					console.error('Error loading user roles with tasks:', rolesWithTasksResult.reason);
					setRolesWithTasks([]);
				}

				await updateUserDocuments(id);
			}
		},
		[navigate, usersResponse?.data, partnerId, updateUserDocuments],
	);

	useEffect(() => {
		if (activeUserId && usersResponse?.data) {
			const user = usersResponse.data.find((u) => u.id === activeUserId);
			if (user) {
				handleRowClick({ id: activeUserId });
			}
		}
	}, [activeUserId, usersResponse?.data, handleRowClick]);

	// Navigation helpers
	const updateQueryParams = useUpdateQueryParams();

	if (!verifyPermission(PERMISSIONS.partnerCompanyUsersView)) {
		navigate({ to: `/partner/${partnerId}` });
		return null;
	}
	// Loading state - gating initial load (preventing losing focus during typing is search)
	if (userLoading || companyLoading || rolesLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner className="text-[#1E1E1E]" />
			</div>
		);
	}

	if (!partnerCompany) {
		return (
			<div className="mt-2 flex flex-col items-center justify-start">
				<div className="flex gap-2">
					<div className="min-w-[320px]">
						<p>No partner company found or no users data available.</p>
					</div>
				</div>
			</div>
		);
	}

	// Event handlers
	const handleSearchChange = debounce((searchValue: string) => {
		updateQueryParams({ search: searchValue });
	}, 350);

	const handleRoleFilterChange = (roleIds: string | string[]) => {
		updateQueryParams({ roleIds });
	};

	const handleRoleStatusChange = (roleStatus: string) => {
		updateQueryParams({ roleStatus });
	};

	const handlePageChange = (newPage: number) => {
		updateQueryParams({ page: newPage });
	};

	const handleSort = ([sort]) => {
		updateQueryParams({ sort: sortToString(sort) });
	};

	const handleCloseSheet = () => {
		navigate({
			search: (prev) => {
				const { activeUserId: _activeUserId, ...rest } = prev;
				return rest;
			},
		});

		setActiveUser(undefined);
		setActiveUserTasks([]);
		setRolesWithTasks([]);
		setActiveUserTodoTasks([]);
		setActiveUserApprovals([]);
		setActiveUserTodoApprovals([]);
		// setIsSheetOpen(false);
		setIsEditingRoles(false);
		setIsEditingUser(false);
	};

	const handleDeleteUser = async () => {
		if (!activeUser) return;

		try {
			await archivePartnerCompanyUser(partnerId, activeUser.id);
			EventEmitter.emit('toast', {
				type: 'success',
				message: 'User archived successfully',
			});
			queryClient.invalidateQueries({
				queryKey: ['partner-company-users', partnerId, { search, roleIds, page, sort, roleStatus }],
			});
			setIsDeleteUserDialogOpen(false);
			// setIsSheetOpen(false);
		} catch (_error) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Error archiving user',
			});
		}
	};

	const handleResendInvite = async () => {
		if (!activeUser) return;

		setIsSendingInvite(true);
		try {
			await resendInviteToPartnerCompanyUser(partnerId, activeUser.id);
			queryClient.invalidateQueries({
				queryKey: ['partner-company-users', partnerId, { search, roleIds, page, sort, roleStatus }],
			});
			setIsInviteSent(true);
			EventEmitter.emit('toast', {
				type: 'success',
				message: 'Invite sent successfully',
			});
		} catch (_error) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to resend invite',
			});
		} finally {
			setIsSendingInvite(false);
		}
	};

	const handleInviteUser = async (data: InviteData) => {
		try {
			await linkUserToPartnerCompany(partnerId, data);
			EventEmitter.emit('toast', { type: 'success', message: 'User invited successfully' });
			queryClient.invalidateQueries({
				queryKey: ['partner-company-users', partnerId, { search, roleIds, page, sort, roleStatus }],
			});
			setIsAddUserDialogOpen(false);
		} catch (_error) {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to invite user.' });
		}
	};

	const handleImportUsers = async (importData: ImportData) => {
		try {
			const result = await importUsersToPartnerCompany(partnerId, importData);
			if (result.success) {
				EventEmitter.emit('toast', { type: 'success', message: 'Users imported successfully' });
				queryClient.invalidateQueries({
					queryKey: ['partner-company-users', partnerId, { search, roleIds, page, sort, roleStatus }],
				});
				setIsAddUserDialogOpen(false);
			} else {
				EventEmitter.emit('toast', { type: 'error', message: 'Failed to import all users.' });
			}
			return result;
		} catch (error: unknown) {
			return importUserFailureFromCaughtError(error);
		}
	};

	const handleRoleAdd = async (roleId: string) => {
		if (!activeUser) return;

		try {
			await addRoleToPartnerCompanyUser(partnerId, activeUser.id, { roleId });
			EventEmitter.emit('toast', { type: 'success', message: 'Role added successfully' });
			queryClient.invalidateQueries({
				queryKey: ['partner-company-users', partnerId, { search, roleIds, page, sort, roleStatus }],
			});
			setRoleDialogOpen(false);
		} catch (_error) {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to add role' });
		}
	};

	const handleRoleRevoke = async (roleId: string) => {
		if (!activeUser) return;

		try {
			await removeRoleFromPartnerCompanyUser(partnerId, activeUser.id, roleId);
			EventEmitter.emit('toast', { type: 'success', message: 'Role removed successfully' });
			queryClient.invalidateQueries({
				queryKey: ['partner-company-users', partnerId, { search, roleIds, page, sort, roleStatus }],
			});
			setRoleToRevokeId(undefined);
		} catch (_error) {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to remove role' });
		}
	};

	const updateUserData = async (userData: any) => {
		if (!activeUser) return;

		try {
			await updatePartnerCompanyUser(partnerId, activeUser.id, userData);
			EventEmitter.emit('toast', { type: 'success', message: 'User updated successfully' });
			queryClient.invalidateQueries({
				queryKey: ['partner-company-users', partnerId, { search, roleIds, page, sort, roleStatus }],
			});
			setIsEditingUser(false);
		} catch (_error) {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to update user' });
		}
	};

	const handleTaskStatusClick = (task: Task, isExecutable: boolean) => {
		if (!isExecutable) return;
		setActiveTask(task);
	};

	const handleTaskDialogClose = async () => {
		setActiveTask(undefined);
		if (activeUser) {
			const userTasksResponse = await getPartnerCompanyUserTasks(partnerId, activeUser.id);
			setActiveUserTasks(userTasksResponse.data || []);
		}
	};

	const renderPhoneNumber = () => {
		try {
			if (activeUser?.phone) {
				return parsePhoneNumber(activeUser.phone)?.formatInternational();
			}
			return '-';
		} catch (_error) {
			return activeUser?.phone || '-';
		}
	};

	// Check if role belongs to group
	const roleBelongsToGroup = (role: Role) => {
		return partnerCompany.possibleRoleIds?.includes(role.id) || partnerCompany.type?.possibleRoleIds?.includes(role.id);
	};

	// Task execution helper
	const handleTaskExecution = (task: Task) => {
		if (task.status !== 'todo') return { isExecutable: false, tooltipText: '' };
		if (!task.executePermission?.isAllowed)
			return { isExecutable: false, tooltipText: task.executePermission?.denyReason ?? '' };
		return { isExecutable: true, tooltipText: '' };
	};

	// Column definitions
	const columns: DataGridColumn<User>[] = [
		{
			field: 'markedForReview',
			headerName: '',
			width: 40,
			sortable: false,
			renderCell: (params) => (params.row.markedForReview ? <AlertTriangle className="h-4 w-4 text-red-500" /> : null),
		},
		{
			field: 'firstName',
			headerName: 'First name',
			flex: 1,
		},
		{
			field: 'lastName',
			headerName: 'Last name',
			flex: 1,
		},
		{
			field: 'roles',
			headerName: 'Roles',
			flex: 1,
			renderCell: (params) => (
				<div className="flex flex-wrap gap-1">
					{params.value?.map((role: Role) => (
						<RoleChip
							key={role.name}
							role={role}
							userMarkedForReview={params.row.markedForReview || params.row.status === 'blocked'}
						/>
					))}
				</div>
			),
			sortable: false,
		},
		{
			field: 'updatedAt',
			headerName: 'Updated',
			width: 120,
			valueFormatter: (params) => new Date(params.value).toLocaleDateString('en-GB'),
		},
	];

	return (
		<div className="p-6">
			{/* Filters and Add User Button */}
			<div className="flex flex-col gap-4 mb-6">
				<div className="flex flex-col sm:flex-row gap-4">
					<FilterSearch
						key="search"
						label="Search User"
						defaultValue={search || ''}
						onChange={debounce((searchInput: string) => {
							handleSearchChange(searchInput);
						}, 350)}
						sx={{ width: '30%', minWidth: '30%', flexGrow: 1 }}
					/>
					<FilterAutoCompleteById
						key="roleFilter"
						label="Filter Roles"
						options={orderedRoles}
						groupBy={(option) => orderedRoles.find((r) => r.id === option)?.group}
						multiple
						value={roleIds || []}
						onChange={(roleIds) => handleRoleFilterChange(roleIds)}
						sx={{ width: '25%', minWidth: '25%', flexGrow: 1 }}
					/>
					<FilterSelect
						key="roleStatus"
						listSubHeader="Filter Role Status"
						options={roleStatusTypes}
						value={roleStatus || 'all'}
						onChange={(roleStatus) => handleRoleStatusChange(roleStatus)}
						sx={{ width: '25%', minWidth: '25%', flexGrow: 1 }}
					/>
					<ConditionalWrapper
						Wrapper={({ children }) => <Tooltip text="This action is not available for your role">{children}</Tooltip>}
						condition={!verifyPermission(PERMISSIONS.partnerCompanyUsersCreate)}
					>
						<Button
							disabled={!verifyPermission(PERMISSIONS.partnerCompanyUsersCreate)}
							variant="outline-bg-white"
							className="h-14 !pointer-events-auto"
							onClick={() => {
								setIsAddUserDialogOpen(true);
							}}
						>
							<Plus />
							ADD USER
						</Button>
					</ConditionalWrapper>
				</div>
			</div>

			{/* Data Grid */}
			<div className="mt-2  border border-gray-300 rounded bg-white shadow-sm">
				<DataGrid
					data={usersResponse?.data ?? []}
					columns={columns}
					rowCount={usersResponse?.count || 0}
					isLoading={usersLoading}
					paginationMode="server"
					sortingMode="server"
					onPaginationModelChange={({ page }) => handlePageChange(page)}
					onSortModelChange={handleSort}
					sortingOrder={['asc', 'desc']}
					sortModel={[stringToSort(sort || 'updatedAt:desc')]}
					pageSize={usersResponse?.pageSize ?? 25}
					pageSizeOptions={[usersResponse?.pageSize ?? 25]}
					page={page}
					onRowClick={handleRowClick}
					pagination
					getRowClassName={(params) => (params.row.markedForReview || params.row.status === 'blocked') && 'error'}
					onReload={() =>
						queryClient.invalidateQueries({
							queryKey: ['partner-company-users', partnerId, { search, roleIds, page, sort, roleStatus }],
						})
					}
				/>
			</div>

			{/* User Details Sheet */}
			<Sheet open={isSheetOpen} onOpenChange={(open) => !open && handleCloseSheet()} modal={false}>
				<SheetContent
					className="w-[600px] sm:max-w-[600px] overflow-y-auto bg-white [&>button]:hidden"
					disableOutsideClick={
						isRoleDialogOpen ||
						isEditingUser ||
						isDeleteUserDialogOpen ||
						!!activeTask ||
						!!selectedFile ||
						!!roleToRevokeId
					}
				>
					{activeUser && (
						<>
							<SheetHeader className="space-y-4">
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-2">
										<Button
											variant="ghost"
											onClick={handleCloseSheet}
											className="focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none"
										>
											<X />
										</Button>
										<SheetTitle className="text-4xl font-bold">User Details</SheetTitle>
									</div>
									<div className="flex items-center gap-2">
										{activeUser.email && verifyPermission([PERMISSIONS.partnerCompanyUsersEdit]) && (
											<Button
												variant="outline-bg-white"
												disabled={isInviteSent || isSendingInvite}
												onClick={handleResendInvite}
												size="lg"
											>
												{isSendingInvite ? 'SENDING...' : isInviteSent ? 'INIVTE SEND' : 'RESEND INVITE'}
											</Button>
										)}
										{verifyPermission([PERMISSIONS.partnerCompanyUsersArchive]) && (
											<Button
												variant="destructive"
												size="lg"
												onClick={() => setIsDeleteUserDialogOpen(true)}
												className="px-5"
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										)}
									</div>
								</div>
							</SheetHeader>

							<div className="space-y-6 mt-6">
								{/* Alerts */}
								{activeUserTodoTasks.length > 0 && (
									<Alert variant="warning" className="flex items-center gap-2">
										<div>
											<AlertTriangle className="h-4 w-4" />
										</div>
										<div>This user has one or more pending tasks.</div>
									</Alert>
								)}

								{activeUserTodoApprovals.length > 0 && (
									<Alert variant="warning" className="flex items-center gap-2">
										<AlertTriangle className="h-4 w-4" />
										<div>This user has one or more pending approvals.</div>
									</Alert>
								)}

								{activeUser.status === 'blocked' && (
									<Alert variant="destructive" className="flex items-center gap-2">
										<AlertTriangle className="h-4 w-4" />
										<div>This user matches one or more blocklist rules. Contact your administrator.</div>
									</Alert>
								)}

								{Array.isArray(activeUser.markedForReview) && activeUser.markedForReview.length > 0 && (
									<Alert variant="destructive" className="flex items-center gap-2">
										<AlertTriangle className="h-4 w-4" />
										<div>This user is currently locked and requires review before receiving access.</div>
									</Alert>
								)}

								{/* User Profile Card */}
								<Card className="bg-white">
									<CardContent className="px-1">
										<div className="flex gap-4">
											<div className="flex w-[200px] justify-self-stretch mr-1">
												<div className="w-[200px] h-[200px] border border-gray-300 rounded-md overflow-hidden bg-gray-100">
													{activeUserProfile?.faceImage?.link ? (
														<img
															src={`/api/partner-portal/groups/${partnerCompany.id}/users/${activeUser.id}/files/${activeUserProfile.faceImage.link.split('/').pop()}`}
															alt={`${activeUser.firstName} ${activeUser.lastName}`}
															className="w-full h-full object-cover"
														/>
													) : (
														<div className="w-full h-full flex items-center justify-center text-gray-400">
															<UserIcon className="h-16 w-16" />
														</div>
													)}
												</div>
											</div>
											<div className="flex-1 space-y-2">
												<h3 className="text-lg font-semibold">
													{activeUser.firstName} {activeUser.lastName}
												</h3>
												<div>
													<p className="text-sm text-muted-foreground">Email</p>
													<p className="text-sm">{activeUser.email || '-'}</p>
												</div>
												<div>
													<p className="text-sm text-muted-foreground">Phone</p>
													<p className="text-sm">{renderPhoneNumber()}</p>
												</div>
											</div>
											{verifyPermission([PERMISSIONS.partnerCompanyUsersEdit]) && (
												<Button variant="ghost" size="sm" onClick={() => setIsEditingUser(!isEditingUser)}>
													<Edit className="h-4 w-4" />
												</Button>
											)}
										</div>
									</CardContent>
								</Card>

								{/* Roles Card */}
								<Card className="bg-white gap-1">
									<CardHeader className="px-1">
										<div className="flex items-center justify-between">
											<CardTitle className="text-lg">Roles</CardTitle>
											{verifyPermission([PERMISSIONS.partnerCompanyUsersEdit]) && (
												<Button variant="ghost" size="sm" onClick={() => setIsEditingRoles(!isEditingRoles)}>
													<Edit className="h-4 w-4" />
												</Button>
											)}
										</div>
									</CardHeader>
									<CardContent className="px-1">
										<div className="flex flex-wrap gap-2">
											{activeUser.roles.length > 0 ? (
												activeUser.roles.map((role: Role) => {
													const canDelete = isEditingRoles && roleBelongsToGroup(role);
													return (
														<RoleChip
															key={role.name}
															role={role}
															onDelete={canDelete ? () => setRoleToRevokeId(role.id) : undefined}
															tooltipCondition={isEditingRoles && !roleBelongsToGroup(role)}
															tooltipText="Role cannot be deleted. It does not belong to this company."
														/>
													);
												})
											) : (
												<p className="text-muted-foreground">-</p>
											)}
											{isEditingRoles && (
												<Badge variant="outline" className="cursor-pointer" onClick={() => setRoleDialogOpen(true)}>
													<Plus className="h-3 w-3 mr-1" /> Add Role
												</Badge>
											)}
										</div>
									</CardContent>
								</Card>

								{/* Tasks Card */}
								{rolesWithTasks.length > 0 && (
									<Card className="bg-white">
										<CardHeader className="px-1">
											<CardTitle className="text-lg">Tasks by User Role</CardTitle>
										</CardHeader>
										<CardContent className="px-1">
											<UserRolesWithTasksAccordion
												rolesWithTasks={rolesWithTasks}
												onTaskSelected={(taskId) => {
													const task = activeUserTasks.find((t) => t.id === taskId);
													if (task) {
														const { isExecutable } = handleTaskExecution(task);
														handleTaskStatusClick(task, isExecutable);
													}
												}}
												onReload={() =>
													queryClient.invalidateQueries({
														queryKey: ['partner-company-users', partnerId, { search, roleIds, page, sort, roleStatus }],
													})
												}
												searchParams={{}}
												expandRoles={false}
												hideDetailColumns={true}
											/>
										</CardContent>
									</Card>
								)}

								{/* Approvals Card */}
								{activeUserApprovals.length > 0 && (
									<Card className="bg-white">
										<CardHeader className="px-1">
											<CardTitle className="text-lg">Approvals</CardTitle>
										</CardHeader>
										<CardContent className="px-1">
											<div className="space-y-2">
												{activeUserApprovals.map((approval) => {
													const { isExecutable, tooltipText } = handleTaskExecution(approval);
													return (
														<div key={approval.id} className="flex justify-between items-center">
															<span className="text-sm">{approval.name}</span>
															<ConditionalWrapper
																condition={!isExecutable}
																Wrapper={({ children }) => <div title={tooltipText}>{children}</div>}
															>
																<Badge
																	variant={variants[approval.status as keyof typeof variants]}
																	className={isExecutable ? 'cursor-pointer' : ''}
																	onClick={() => handleTaskStatusClick(approval, isExecutable)}
																>
																	{approval.status}
																</Badge>
															</ConditionalWrapper>
														</div>
													);
												})}
											</div>
										</CardContent>
									</Card>
								)}

								{/* Documents Card */}
								{verifyPermission([PERMISSIONS.partnerCompanyUsersView]) && (
									<Card className="bg-white">
										<CardHeader className="px-1">
											<CardTitle className="text-lg">Documents</CardTitle>
										</CardHeader>
										<CardContent className="px-1">
											<div className="space-y-2">
												{activeUserDocuments.length > 0 ? (
													activeUserDocuments.map((document) => (
														<div
															key={document.id}
															className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-gray-50"
															onClick={() => {
																navigate({
																	to: '/partner/$partnerId/users/$userId/$documentId',
																	params: { partnerId, userId: activeUser.id, documentId: document.id },
																	// Preserve all current search params
																	search: (prev) => prev,
																});
																return null;
															}}
														>
															<FileState status={document.status} />
															<span className="flex-1 text-sm">{document.fileName}</span>
															<span className="text-xs text-muted-foreground">{document.type}</span>
														</div>
													))
												) : (
													<p className="text-muted-foreground">-</p>
												)}
											</div>
										</CardContent>
									</Card>
								)}
							</div>
						</>
					)}
				</SheetContent>
			</Sheet>

			<InviteForm
				onClose={() => setIsAddUserDialogOpen(false)}
				isOpen={isAddUserDialogOpen}
				onInvite={handleInviteUser}
				onImport={handleImportUsers}
				roles={roles}
				selectedGroup={partnerCompany}
				authenticatedUser={authenticatedUser}
				defaultCountry={defaultCountry} // You might want to get this from settings
				possibleRoleIds={[...(partnerCompany.possibleRoleIds || []), ...(partnerCompany.type?.possibleRoleIds || [])]}
				permissions={{
					canCreateWithoutEmail: false,
					isUserAdmin: false,
					canImport: true,
				}}
			/>

			{activeUser && <TaskDialog task={activeTask} onClose={handleTaskDialogClose} userId={activeUser.id} />}

			{activeUser && (
				<ConfirmDialog
					isOpen={!!roleToRevokeId}
					cancelAction={() => setRoleToRevokeId(undefined)}
					confirmAction={() => roleToRevokeId && handleRoleRevoke(roleToRevokeId)}
					cancelLabel="Cancel"
					confirmLabel="Delete"
				>
					Are you sure you want to delete this role?
				</ConfirmDialog>
			)}

			{activeUser && (
				<UserForm
					isOpen={isEditingUser}
					isAdmin={false}
					onClose={() => setIsEditingUser(false)}
					onSubmit={updateUserData}
					data={activeUser}
					authenticatedUser={authenticatedUser}
					defaultCountry={defaultCountry}
				/>
			)}

			{activeUser && (
				<ConfirmDialog
					isOpen={isDeleteUserDialogOpen}
					cancelAction={() => setIsDeleteUserDialogOpen(false)}
					confirmAction={handleDeleteUser}
					cancelLabel="Cancel"
					confirmLabel="Archive"
				>
					Are you sure you want to archive this user?
				</ConfirmDialog>
			)}

			{activeUser && (
				<AddToRoleDialog
					isOpen={isRoleDialogOpen}
					onClose={() => setRoleDialogOpen(false)}
					userData={activeUser}
					roles={roles}
					onRoleAdd={handleRoleAdd}
				/>
			)}

			{activeUser && selectedFile && (
				<DocumentViewer
					document={selectedFile}
					onClose={() => setSelectedFile(null)}
					types={[]} // You might want to get document types from API
					apiUrlBase={`/api/partner-portal/groups/${partnerCompany.id}/users/${activeUser.id}/files`}
					onRevalidateDocument={() => {
						updateUserDocuments(activeUser.id);
					}}
					tasks={[]}
					permissions={{
						metadata: verifyPermission([PERMISSIONS.partnerCompanyUsersView]),
						fullView: verifyPermission([PERMISSIONS.partnerCompanyUsersView]),
						sensitiveView: false,
						edit: false,
						archive: false,
						review: false,
					}}
				/>
			)}
		</div>
	);
}
